import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from "@/store/useAuthStore.jsx";

// 경로별 필요 권한 정의
const ROUTE_PERMISSIONS = {
    // ADMIN 전용 경로
    '/reprint': 'ROLE_ADMIN',
    '/media-list': 'ROLE_ADMIN',
    '/branch-management': 'ROLE_ADMIN',
    '/account-list': 'ROLE_ADMIN',
    '/account-group-management': 'ROLE_ADMIN',
    '/design-management': 'ROLE_ADMIN',
    '/design-management/page-design': 'ROLE_ADMIN',
    '/camera-angle-management': 'ROLE_ADMIN',
    '/photo-layout-management': 'ROLE_ADMIN',
    '/photo-filter-management': 'ROLE_ADMIN',
    '/photo-frame-management': 'ROLE_ADMIN',

    // MANAGER 이상 경로
    '/page-preset-management': 'ROLE_MANAGER',
    '/page-preset-management/page-preset': 'ROLE_MANAGER',

    // USER 이상 경로 (기본 - 모든 인증 사용자 접근 가능)
    '/manual': 'ROLE_USER',
    '/dashboard': 'ROLE_USER',
    '/': 'ROLE_USER',
};

// 인증이 필요한 컴포넌트를 감싸는 보호 컴포넌트
export default function ProtectedRoute({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, syncAuthState, hasRoleOrHigher } = useAuthStore();
    const [isChecking, setIsChecking] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);

    useEffect(() => {
        let isCancelled = false;
        const currentPath = location.pathname;

        const checkAuth = async () => {
            setIsChecking(true);
            setHasPermission(false);
            try {
                const isValid = await syncAuthState();

                if (isCancelled) return;

                if (!isValid) {
                    navigate('/login', { replace: true });
                    return;
                }

                // 권한 체크 (useEffect 시작 시점의 경로 사용)
                const requiredRole = getRequiredRole(currentPath);
                const permitted = hasRoleOrHigher(requiredRole);

                if (!permitted) {
                    // Modified: 2025-01-27 [현재 시간 추가 필요] - Temporarily redirect to manual instead of dashboard
                    const errorMsg = encodeURIComponent('접근 권한이 없습니다.');
                    navigate(`/manual?error_msg=${errorMsg}`, { replace: true });
                    return;
                }

                setHasPermission(true);
            } catch (error) {
                if (!isCancelled) {
                    navigate('/login', { replace: true });
                }
            } finally {
                if (!isCancelled) {
                    setIsChecking(false);
                }
            }
        };

        checkAuth();

        return () => {
            isCancelled = true;
        };
    }, [location.pathname]);

    // 경로에 필요한 권한 반환
    const getRequiredRole = (path) => {
        let requiredRole = ROUTE_PERMISSIONS[path];

        if (!requiredRole) {
            const pathParts = path.split('/').filter(Boolean);
            for (let i = pathParts.length; i > 0; i--) {
                const parentPath = '/' + pathParts.slice(0, i).join('/');
                if (ROUTE_PERMISSIONS[parentPath]) {
                    requiredRole = ROUTE_PERMISSIONS[parentPath];
                    break;
                }
            }
        }

        return requiredRole || 'ROLE_USER';
    };

    // 인증 확인 중이거나 권한이 없으면 로딩 표시
    if (isChecking || !hasPermission) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div>인증 확인 중...</div>
            </div>
        );
    }

    // 인증되고 권한이 있는 경우에만 자식 컴포넌트 렌더링
    return isAuthenticated && hasPermission ? children : null;
}